import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getCsrfCookie } from "../lib/utils";
import { REEPRISE_LIST_QUERY_KEY } from "./useGeneratedTextPaginatedList";
import { TEXT_STATISTICS_QUERY_KEY } from "./useTextStatistics";
import { invalidateQuery as invalidateStatistics } from "./useVocabularyStatistics";
import { GeneratedText } from "./types/GeneratedTexts";
import { REEPRISE_DETAILS_QUERY_KEY } from "./useGeneratedTextDetails";

export type ImageReadingTextWorkflow =
  | "IMPORT_TEXT"
  | "ADAPT_TO_LEVEL";

export type ImageReadingSelectionBlock = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type ImageReadingArticleCandidateBlock = ImageReadingSelectionBlock & {
  role?: string;
};

export type ImageReadingArticleCandidate = {
  id: string;
  title: string;
  preview: string;
  confidence: number;
  bbox: ImageReadingSelectionBlock;
  blocks?: ImageReadingArticleCandidateBlock[];
};

export type ImageReadingArticleCandidateDiagnostic = {
  id: string;
  title: string;
  preview: string;
  confidence: number;
  status: "accepted" | "rejected";
  source: "ai" | "layout_fallback";
  rejection_reason?:
    | "outside_image_bounds"
    | "missing_geometry"
    | "confidence_too_low_for_single_article"
    | null;
};

export type ImageReadingLayoutAnalysis = {
  selection_required: boolean;
  selection_mode: "article_candidates" | "paint_snap_blocks" | "single_flow";
  block_count: number;
  column_cluster_count: number;
  row_segment_count?: number;
  image_width: number;
  image_height: number;
  blocks: ImageReadingSelectionBlock[];
  detection_status?: "single" | "multiple" | "none";
  candidates?: ImageReadingArticleCandidate[];
  candidate_diagnostics?: ImageReadingArticleCandidateDiagnostic[];
};

export function useGeneratedTextCreateFromImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (arg: {
      file: File;
      language: string;
      textType: string;
      workflow: ImageReadingTextWorkflow;
      selectionBlocks?: ImageReadingSelectionBlock[];
    }) => {
      const formData = new FormData();
      formData.append("image", arg.file);
      formData.append("language", arg.language);
      formData.append("text_type", arg.textType);
      formData.append("workflow", arg.workflow);
      if (arg.selectionBlocks?.length) {
        formData.append("selection_blocks", JSON.stringify(arg.selectionBlocks));
      }

      const res = await fetch(
        "/api/reeprise/generated_texts/image-reading-text/",
        {
          method: "POST",
          headers: {
            "X-CSRFTOKEN": getCsrfCookie(),
          },
          body: formData,
        },
      );
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          throw new Error("AUTH_REQUIRED");
        }
        let errorMessage = "Could not create reading text from image";
        try {
          const body = await res.json();
          const selectionErrors = body?.selection_blocks;
          if (Array.isArray(selectionErrors) && selectionErrors.length > 0) {
            const firstError = selectionErrors[0];
            if (typeof firstError === "string" && firstError.trim()) {
              errorMessage = firstError;
            }
          } else if (typeof body?.detail === "string" && body.detail.trim()) {
            errorMessage = body.detail;
          }
        } catch {
          // Ignore non-JSON error bodies and keep the generic message.
        }
        throw new Error(errorMessage);
      }
      return (await res.json()) as GeneratedText;
    },
    onSettled: (data) => {
      if (data !== undefined) {
        void queryClient.setQueryData(
          [REEPRISE_DETAILS_QUERY_KEY, `${data.id}`],
          data,
        );
      }
      void queryClient.invalidateQueries({
        queryKey: [REEPRISE_LIST_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [TEXT_STATISTICS_QUERY_KEY],
      });
      invalidateStatistics(queryClient);
    },
  });
}

export function useImageReadingLayoutAnalysis() {
  return useMutation({
    mutationFn: async (arg: { file: File }) => {
      const formData = new FormData();
      formData.append("image", arg.file);
      const res = await fetch(
        "/api/reeprise/generated_texts/image-reading-layout/",
        {
          method: "POST",
          headers: {
            "X-CSRFTOKEN": getCsrfCookie(),
          },
          body: formData,
        },
      );
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          throw new Error("AUTH_REQUIRED");
        }
        throw new Error("Could not analyze reading image");
      }
      return (await res.json()) as ImageReadingLayoutAnalysis;
    },
  });
}
